import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ButtonHoverAnimation from "./button/ButtonHoverAnimation";
import { useAccountInfo } from "../utils/constants";
import {
  BrainCreditsBalance,
  PepecoinBalance,
  usePepecoinBalance,
} from "./balances";
import {
  useWriteContract,
  useReadContract,
  useAccount,
  useWaitForTransactionReceipt,
} from "wagmi";
import { parseAbi, parseEther, formatUnits, formatEther } from "viem";
import { creditsABI } from "../utils/creditsABI";
import {
  useEstimateCreditProceeds,
  useEstimatePricePerCredit,
  useMintFrozen,
} from "../utils/hooks";
import BurnSuccess from "./burnsuccess";
import SvgSpinner from "./svgspinner";

const brainCA = process.env.REACT_APP_BRAIN_CREDITS;
const pepecoinCA = process.env.REACT_APP_PEPECOIN_CONTRACT;

const pepecoinABI = parseAbi([
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
]);

const Burning = () => {
  const { shortenedAddress, fullAddress } = useAccountInfo();
  const [pepecoinToBurn, setPepecoinToBurn] = useState(0);
  const [maxPrice, setMaxPrice] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoPrice, setAutoPrice] = useState(true);

  const settingsRef = useRef(null);

  const handleSettingsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Delay the state update slightly
    setTimeout(() => {
      setSettingsOpen((prevState) => !prevState);
    }, 10);
  };

  useEffect(() => {
    function handleOutsideClick(event) {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target) &&
        !event.target.closest(".settings-btn")
      ) {
        setSettingsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [settingsRef]);

  const handleToggleAutoPrice = () => {
    setAutoPrice(!autoPrice);
  };

  const {
    data: approvalHash,
    error: approvalError,
    isPending: isApprovalPending,
    writeContract: writeApprovalContract,
  } = useWriteContract();

  const {
    data: mintHash,
    error: mintError,
    isPending: isMintPending,
    writeContract: writeMintContract,
  } = useWriteContract();

  // Separate useWaitForTransactionReceipt hooks
  const { isLoading: isApprovalConfirming, isSuccess: isApprovalConfirmed } =
    useWaitForTransactionReceipt({
      hash: approvalHash,
    });

  const { isLoading: isMintConfirming, isSuccess: isMintConfirmed } =
    useWaitForTransactionReceipt({
      hash: mintHash,
    });

  const account = useAccount();

  const limitPer1000 = 100000;

  const pepecoinToBurnUint256 = parseEther((pepecoinToBurn || "0").toString());

  const limitUnit256 = parseEther(limitPer1000.toString());

  const { data: allowance } = useReadContract({
    address: pepecoinCA,
    abi: pepecoinABI,
    functionName: "allowance",
    args: [account.address, brainCA],
    query: {
      refetchInterval: 3000,
    },
  });

  // Convert allowance to a number
  const formattedAllowance = allowance ? Number(formatUnits(allowance, 18)) : 0;

  // Ensure pepecoinToBurn is also a number
  const pepecoinToBurnNumber = Number(pepecoinToBurn);

  const isAllowed = formattedAllowance >= pepecoinToBurnNumber;

  const { data: estimatedCreditProceeds, isLoading: isEstimateLoading } =
    useEstimateCreditProceeds(pepecoinToBurn || "0");

  const { data: estimatedExchangeRate, isLoading: isExchangeRateLoading } =
    useEstimateCreditProceeds(1);

  const { data: mintFrozen, isLoading: mintFozenIsLoading } = useMintFrozen();

  const { data: pricePerCredit, isLoading: isPriceLoading } =
    useEstimatePricePerCredit();

  const pepecoinBalance = usePepecoinBalance();

  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handlePepecoinChange = (event) => {
    const value = event.target.value;
    setPepecoinToBurn(value === "" ? "" : Number(value));
  };
  const isMobile = window.innerWidth <= 575.98;

  function approvePepecoin() {
    const pepecoinToBurnString = pepecoinToBurn.toString();
    const pepecoinToBurnEther = parseEther(pepecoinToBurnString);

    writeApprovalContract({
      address: pepecoinCA,
      abi: pepecoinABI,
      functionName: "approve",
      args: [process.env.REACT_APP_BRAIN_CREDITS, pepecoinToBurnEther],
    });
  }

  async function mintCredits(e) {
    e.preventDefault();
    console.log("Contract Address:", brainCA);
    console.log("Pepecoin to Burn:", pepecoinToBurn);
    console.log("Attempting to mint...");

    // Check if approval is necessary
    if (!isAllowed) {
      console.log("Approval needed. Approving Pepecoin...");
      await approvePepecoin();
      console.log("Approval complete. Proceeding with minting...");
    }

    let maxPriceArg = "0";
    if (!autoPrice && maxPrice && Number(maxPrice) > 0) {
      maxPriceArg = parseEther((Number(maxPrice) * 1000).toString()).toString();
    }

    // Proceed with minting
    writeMintContract({
      address: brainCA,
      abi: creditsABI,
      functionName: "mint",
      args: [pepecoinToBurnUint256, maxPriceArg],
    });
  }

  const fomattedExchangeRate = estimatedExchangeRate
    ? formatUnits(estimatedExchangeRate, 18)
    : "0";

  const buttonDisabled =
    pepecoinBalance < pepecoinToBurn ||
    isApprovalPending ||
    isMintPending ||
    isApprovalConfirming ||
    isMintConfirming;

  const getButtonText = () => {
    if (isApprovalPending || isApprovalConfirming) {
      return "Approving...";
    }
    if (isMintPending || isMintConfirming) {
      return "Burning...";
    }
    if (pepecoinBalance < pepecoinToBurn) {
      return "Get More Pepecoin";
    }
    if (!isAllowed) {
      return "Approve Pepecoin";
    }

    return "Burn";
  };

  return (
    <>
      <ButtonHoverAnimation />
      <main data-barba="container" data-barba-namespace="secondary">
        <div className="min-svh-100 d-flex align-items-center justify-content-center px-3 px-sm-4 px-md-5  opacity-load">
          <div className="container w-100">
            <div className="spacer"></div>
            <div className="my-4">
              <ul className="nav-secondary">
                <li className="h1 current">
                  <Link to="/burning">Burn</Link>
                </li>

                <li className="h1">
                  <Link to="/burning-redeem">Redeem</Link>
                </li>
              </ul>

              <div className="mt-4 mt-lg-5">
                <div className="box mx-auto">
                  <div className="box-head py-3 py-md-4 px-3 px-md-5 d-flex justify-content-between align-items-end">
                    <div>
                      <small className="font-mono text-uppercase d-block mb-2 d-flex align-items-center">
                        <span className="muted">Pepecoin Available</span>{" "}
                      </small>
                      <p className="fs-3 mb-0 lh-1 text-uppercase">
                        <PepecoinBalance />
                      </p>
                    </div>
                    <div className="text-end">
                      <em>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="21"
                          height="20"
                          viewBox="0 0 21 20"
                          fill="none"
                          className="position-relative"
                          style={{ top: "-2px" }}
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10.0605 0H16.0605C17.7392 0 19.1763 1.03408 19.7697 2.5H6.35123C6.94462 1.03408 8.38178 0 10.0605 0ZM0.0604858 10V14H4.06049C5.16506 14 6.06049 13.1046 6.06049 12C6.06049 10.8954 5.16506 10 4.06049 10H0.0604858ZM20.0605 4H4.06049C1.85135 4 0.0604858 5.79086 0.0604858 8V8.5H4.06049C5.99348 8.5 7.56049 10.067 7.56049 12C7.56049 13.933 5.99348 15.5 4.06049 15.5H0.0604858V16C0.0604858 18.2091 1.85135 20 4.06049 20H16.0605C18.2696 20 20.0605 18.2091 20.0605 16V4Z"
                            fill="#75FBFD"
                          ></path>
                        </svg>
                      </em>
                      <span className="ps-1">{shortenedAddress}</span>
                      <div className="font-mono text-blue text-uppercase mb-1">
                        <BrainCreditsBalance /> CREDITS
                      </div>
                    </div>
                  </div>
                  <hr className="my-0 border-green" />
                  <div className="pt-3 pt-md-4 px-3 px-md-5 burn-container">
                    <form
                      action=""
                      className={isMintConfirmed ? "submitted" : ""}
                    >
                      <div className="form mt-2">
                        <div className="row">
                          <div className="col-sm-12 settings-col">
                            <button
                              className="settings-btn"
                              onClick={handleSettingsClick}
                              onTouchEnd={handleSettingsClick}
                              type="button"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M20.83 14.6C19.9 14.06 19.33 13.07 19.33 12C19.33 10.93 19.9 9.93999 20.83 9.39999C20.99 9.29999 21.05 9.1 20.95 8.94L19.28 6.06C19.22 5.95 19.11 5.89001 19 5.89001C18.94 5.89001 18.88 5.91 18.83 5.94C18.37 6.2 17.85 6.34 17.33 6.34C16.8 6.34 16.28 6.19999 15.81 5.92999C14.88 5.38999 14.31 4.41 14.31 3.34C14.31 3.15 14.16 3 13.98 3H10.02C9.83999 3 9.69 3.15 9.69 3.34C9.69 4.41 9.12 5.38999 8.19 5.92999C7.72 6.19999 7.20001 6.34 6.67001 6.34C6.15001 6.34 5.63001 6.2 5.17001 5.94C5.01001 5.84 4.81 5.9 4.72 6.06L3.04001 8.94C3.01001 8.99 3 9.05001 3 9.10001C3 9.22001 3.06001 9.32999 3.17001 9.39999C4.10001 9.93999 4.67001 10.92 4.67001 11.99C4.67001 13.07 4.09999 14.06 3.17999 14.6H3.17001C3.01001 14.7 2.94999 14.9 3.04999 15.06L4.72 17.94C4.78 18.05 4.89 18.11 5 18.11C5.06 18.11 5.12001 18.09 5.17001 18.06C6.11001 17.53 7.26 17.53 8.19 18.07C9.11 18.61 9.67999 19.59 9.67999 20.66C9.67999 20.85 9.82999 21 10.02 21H13.98C14.16 21 14.31 20.85 14.31 20.66C14.31 19.59 14.88 18.61 15.81 18.07C16.28 17.8 16.8 17.66 17.33 17.66C17.85 17.66 18.37 17.8 18.83 18.06C18.99 18.16 19.19 18.1 19.28 17.94L20.96 15.06C20.99 15.01 21 14.95 21 14.9C21 14.78 20.94 14.67 20.83 14.6ZM12 15C10.34 15 9 13.66 9 12C9 10.34 10.34 9 12 9C13.66 9 15 10.34 15 12C15 13.66 13.66 15 12 15Z"
                                  fill="currentColor"
                                ></path>
                              </svg>
                            </button>
                            <AnimatePresence>
                              {settingsOpen && (
                                <motion.div
                                  ref={settingsRef}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 10 }}
                                  transition={{ duration: 0.2 }}
                                  style={{
                                    position: "absolute",
                                    right: "1.5rem",
                                    top: "3rem",
                                    zIndex: 1000,
                                    background: "rgb(19, 19, 19)",
                                    border:
                                      "1px solid rgba(255, 255, 255, 0.15)",
                                    padding: "1rem",
                                    borderRadius: "14px",
                                    boxShadow:
                                      "rgba(0, 0, 0, 0.01) 0px 0px 1px, rgba(0, 0, 0, 0.04) 0px 4px 8px, rgba(0, 0, 0, 0.04) 0px 16px 24px, rgba(0, 0, 0, 0.01) 0px 24px 32px",
                                  }}
                                  className="settings-tooltip"
                                >
                                  <div className="row mt-2">
                                    <div className="col-6">
                                      <small className="font-mono">
                                        Max. Price
                                      </small>
                                    </div>
                                    <div className="col-6">
                                      <small
                                        className="font-mono"
                                        style={{
                                          color: "cyan",
                                          float: "right",
                                        }}
                                      >
                                        {autoPrice ? "Auto" : "Custom"}
                                      </small>
                                    </div>
                                  </div>
                                  <div className="row mt-2">
                                    <div className="col-12">
                                      <small className="font-mono muted">
                                        Set a maximum price (in Pepecoin) that
                                        you are willing to pay per Brain Credit.
                                      </small>
                                    </div>
                                  </div>
                                  <div className="row mt-2">
                                    <div className="col-6">
                                      <div
                                        className="switches-container"
                                        onClick={handleToggleAutoPrice}
                                        onTouchStart={(e) => {
                                          e.preventDefault();
                                          handleToggleAutoPrice();
                                        }}
                                      >
                                        <input
                                          type="radio"
                                          id="switchAuto"
                                          name="switchMode"
                                          value="Auto"
                                          checked={autoPrice}
                                          readOnly
                                        />
                                        <input
                                          type="radio"
                                          id="switchCustom"
                                          name="switchMode"
                                          value="Custom"
                                          checked={!autoPrice}
                                          readOnly
                                        />
                                        <label htmlFor="switchAuto">Auto</label>
                                        <label htmlFor="switchCustom">
                                          Custom
                                        </label>
                                        <div className="switch-wrapper">
                                          <div className="switch">
                                            <div>Auto</div>
                                            <div>Custom</div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <input
                                        className="max-price-input"
                                        type="number"
                                        id="maxPrice"
                                        value={maxPrice}
                                        onChange={(e) => {
                                          setAutoPrice(false);
                                          setMaxPrice(e.target.value);
                                        }}
                                        placeholder={
                                          autoPrice ? "Auto" : "Pepecoins"
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div className="row mt-2">
                                    <div className="col-12">
                                      <small
                                        className="font-mono muted"
                                        style={{
                                          fontSize: "10px",
                                        }}
                                      >
                                        * Note: If the price moves higher than
                                        your maximum before your transaction is
                                        confirmed, the transaction will fail.
                                      </small>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-7 mb-3">
                            <label
                              htmlFor="InputPepcoin"
                              className="form-label d-block small opacity-75 font-mono ps-3"
                            >
                              Pepecoin to Burn
                            </label>
                            <div className="position-relative">
                              <div className="input-icon p-3">
                                <img src="img/icon-pepe-coin.png" alt="" />
                              </div>
                              <input
                                type="number"
                                className="form-control ps-6"
                                id="InputPepcoin"
                                placeholder="Pepecoin"
                                value={pepecoinToBurn}
                                onChange={handlePepecoinChange}
                              />
                              <div className="input-text small opacity-75 font-mono text-uppercase p-3 d-flex align-items-center">
                                <button
                                  className="max-btn"
                                  type="button"
                                  onClick={() =>
                                    setPepecoinToBurn(pepecoinBalance)
                                  }
                                >
                                  max
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-5 mb-3">
                            <label
                              htmlFor="InputBrains"
                              className="form-label d-block small opacity-75 font-mono ps-3"
                            >
                              Est. Brain Credits
                            </label>
                            <div className="position-relative">
                              <span className="form-control">
                                {isEstimateLoading
                                  ? "Loading..."
                                  : estimatedCreditProceeds
                                  ? Number(
                                      formatUnits(estimatedCreditProceeds, 18)
                                    )
                                      .toFixed(4)
                                      .replace(/\.?0+$/, "")
                                  : "0"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={mintCredits}
                          disabled={buttonDisabled}
                        >
                          {getButtonText()}
                        </button>
                        <div className="pt-4">
                          <div
                            className="d-sm-flex justify-content-between mb-3 mb-sm-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => setSettingsOpen(!settingsOpen)}
                          >
                            <small className="font-mono muted text-uppercase">
                              Max. Price
                            </small>
                            <small className="font-mono text-uppercase d-block">
                              {autoPrice
                                ? "Auto"
                                : `${maxPrice} Pepecoin / Credit`}
                            </small>
                          </div>
                          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
                            <small className="font-mono muted text-uppercase">
                              Est. Credits
                            </small>
                            <small className="font-mono text-uppercase d-block">
                              {isEstimateLoading ? (
                                <SvgSpinner />
                              ) : estimatedCreditProceeds ? (
                                `${Number(
                                  formatUnits(estimatedCreditProceeds, 18)
                                )
                                  .toFixed(4)
                                  .replace(/\.?0+$/, "")} Brain Credits`
                              ) : (
                                "Awaiting Input"
                              )}
                            </small>
                          </div>
                          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
                            <small className="font-mono muted text-uppercase d-block">
                              Est. Exchange Rate
                            </small>
                            <small className="font-mono text-uppercase d-block">
                              {isEstimateLoading || isPriceLoading ? (
                                <SvgSpinner />
                              ) : pepecoinToBurn &&
                                Number(pepecoinToBurn) > 0 &&
                                estimatedCreditProceeds ? (
                                `1 PEPECOIN = ${(
                                  Number(
                                    formatUnits(estimatedCreditProceeds, 18)
                                  ) / Number(pepecoinToBurn)
                                )
                                  .toFixed(4)
                                  .replace(/\.?0+$/, "")} BRAIN CREDITS`
                              ) : estimatedExchangeRate ? (
                                `1 PEPECOIN = ${Number(
                                  formatUnits(estimatedExchangeRate, 18)
                                )
                                  .toFixed(4)
                                  .replace(/\.?0+$/, "")} BRAIN CREDITS`
                              ) : (
                                "N/A"
                              )}
                            </small>
                          </div>
                          {/* <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
                            <small className="font-mono muted text-uppercase d-block">
                              * credits received may vary
                            </small>
                          </div> */}
                        </div>
                      </div>
                      <BurnSuccess
                        coinsBurned={pepecoinToBurn}
                        hash={mintHash}
                      />
                    </form>
                  </div>

                  <div className="pb-3 pb-md-4 px-3 px-md-5">
                    {isMobile && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: isVisible ? "auto" : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="readMoreContent">
                          <hr className="my-0 my-sm-4" />
                          <ul className="list-1">
                            <li>
                              Burn any amount of Pepecoin in exchange for Brain
                              Credits.
                            </li>
                            <li>
                              Exchange rate increases in cost with each
                              additional{" "}
                              <strong> 1000 Brain Credits minted</strong>.
                            </li>
                            <li>
                              Brain Credits are transferable{" "}
                              <strong>ERC-20 tokens</strong>.
                            </li>
                          </ul>
                        </div>
                      </motion.div>
                    )}
                    {!isMobile && (
                      <div className="readMoreContent read-more-content-mobile">
                        <hr className="my-0 my-sm-4" />
                        <ul className="list-1">
                          <li>
                            Burn any amount of Pepecoin in exchange for Brain
                            Credits.
                          </li>
                          <li>
                            Exchange rate increases in cost with each additional{" "}
                            <strong> 1000 Brain Credits minted</strong>.
                          </li>
                          <li>
                            Brain Credits are transferable{" "}
                            <strong>ERC-20 tokens</strong>.
                          </li>
                        </ul>
                      </div>
                    )}
                    <div
                      className={`readMore d-sm-none ${
                        isVisible ? "open" : ""
                      }`}
                      onClick={toggleVisibility}
                    >
                      <strong>
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="12.0068"
                            cy="12.9766"
                            r="12"
                            transform="rotate(90 12.0068 12.9766)"
                            fill="#75FBFD"
                          />
                          <path
                            d="M15.249 11.8496L12.0073 14.1041L8.76562 11.8496"
                            stroke="black"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </strong>
                      <span className="ps-1">
                        {isVisible ? "Close" : "Read More"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* TODO: Change to Stake Brains */}
              <Link
                className="underline-on-hover-2 d-flex align-items-center mx-auto mt-4 justify-content-center"
                to="/acquire-brain"
              >
                {/* <Link
                className="underline-on-hover-2 d-flex align-items-center mx-auto mt-4 justify-content-center"
                to="/"
              > */}
                <em>
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12.4526" cy="12.3848" r="12" fill="#75FBFD" />
                    <path
                      d="M13.7493 15.6265L11.1559 12.3848L13.7493 9.14306"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </em>
                <span className="ps-2">Go Back</span>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Burning;
